/**
 * AdminAutoBlastCard — segment-proportional WhatsApp blast per city.
 *
 * Admin enters a city name + number of messages. The backend computes
 * per-segment quotas based on city composition (decorator 800/6301 →
 * 14% → 14 msgs out of 100) and fires the WhatsApp invite to the
 * picked phones using each contact's saved name in the template.
 *
 * Already-sent phones are tracked per city so subsequent blasts don't
 * spam the same people. When everyone in the city has been touched
 * once, the next blast auto-starts a new round; admin can also click
 * "Reset" to start over immediately.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaperPlaneTilt, ChartBar, ArrowsClockwise, MapPin, ListChecks } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Approved MSG91 WhatsApp templates. Keep this list in sync with what the
// admin has actually approved with their DLT / MSG91 account — the name
// must match the MSG91 template exactly (case-sensitive).
// Tamil template is the default — admin runs blasts primarily in Tamil
// across Tamil Nadu cities. English is kept as an alternate for non-TN.
const TEMPLATES = [
  {
    name: 'deco_join_reuest_tamil',
    label: 'தமிழ் — deco_join_reuest_tamil',
    preview: 'வணக்கம் {name}\nதிருமணம் மற்றும் பிற நிகழ்ச்சிகளுக்கான அதிகமான ஆர்டர்கள் கிடைத்து வருகின்றன. BeeWeddy-யில் இணைந்து, வாடிக்கையாளர்களுடன் நேரடியாக பேசுங்கள்.',
  },
  {
    name: 'pro_join_request',
    label: 'English — pro_join_request',
    preview: 'Dear {name}, please join BeeWeddy platform, list your price per event, get confirmed orders. no hussling on price. Deal with the clients directly.',
  },
];

const AdminAutoBlastCard = () => {
  const [city, setCity] = useState('Chennai');
  const [total, setTotal] = useState(100);
  const [template, setTemplate] = useState(TEMPLATES[0].name);
  const currentTpl = TEMPLATES.find((t) => t.name === template) || TEMPLATES[0];
  const [state, setState] = useState(null);
  const [preview, setPreview] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Manual per-segment allocation override — kept as a string map so
  // typing a partial number doesn't snap back to 0.
  const [allocOverride, setAllocOverride] = useState({});

  const allocSum = Object.values(allocOverride).reduce(
    (a, v) => a + (parseInt(v || '0', 10) || 0), 0,
  );

  const loadCities = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/marketing/contacts?limit=1`,
        { withCredentials: true });
      setAllCities(data?.cities || []);
    } catch {/* non-critical */}
  };

  const refreshState = async (cityName = city) => {
    if (!cityName.trim()) return;
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/marketing/auto-blast/state?city=${encodeURIComponent(cityName.trim())}`,
        { withCredentials: true },
      );
      setState(data);
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error(err?.response?.data?.detail || 'Could not load state');
      }
    }
  };

  // Initial load + reload-on-city-change.
  useEffect(() => { loadCities(); refreshState(); }, []); // eslint-disable-line
  useEffect(() => { refreshState(); }, [city]); // eslint-disable-line

  const runPreview = async () => {
    if (!city.trim() || total <= 0) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/auto-blast`,
        { city: city.trim(), total, preview: true },
        { withCredentials: true },
      );
      setPreview(data);
      setResult(null);
      // Seed the editable per-segment map with the proportional default.
      const seed = {};
      Object.entries(data?.allocation || {}).forEach(([seg, n]) => { seed[seg] = String(n); });
      setAllocOverride(seed);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    }
  };

  const runBlast = async () => {
    if (!city.trim() || total <= 0) return;
    // Build override payload only if admin actually edited any cell.
    const overrideMap = {};
    Object.entries(allocOverride).forEach(([seg, v]) => {
      const n = parseInt(v || '0', 10) || 0;
      if (n > 0) overrideMap[seg] = n;
    });
    const useOverride = Object.keys(overrideMap).length > 0
      && Math.abs(allocSum - total) > 0; // user changed the split away from default
    const finalTotal = useOverride ? allocSum : total;
    if (!window.confirm(
      `Send ${finalTotal} WhatsApp invite${finalTotal === 1 ? '' : 's'} to "${city.trim()}"${useOverride ? ' (manual per-segment split)' : ' (proportional split)'}?\n\nAlready-sent contacts in this round will be skipped.`
    )) return;
    setRunning(true);
    try {
      const body = useOverride
        ? { city: city.trim(), total: finalTotal, preview: false, allocation: overrideMap, template }
        : { city: city.trim(), total, preview: false, template };
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/auto-blast`,
        body, { withCredentials: true, timeout: 240000 },
      );
      setResult(data);
      setPreview(null);
      setAllocOverride({});
      const msg = data?.round_complete
        ? `Sent ${data.sent_ok} · Round complete (${data.rounds_completed} round${data.rounds_completed === 1 ? '' : 's'} done). Next blast starts a new round.`
        : `Sent ${data.sent_ok} · ${data.sent_this_round_after}/${data.total_in_city} reached so far.`;
      if (data?.failed > 0) toast.warning(`${msg} ${data.failed} failed.`, { duration: 10000 });
      else toast.success(msg, { duration: 9000 });
      await refreshState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Blast failed');
    } finally {
      setRunning(false);
    }
  };

  const resetCity = async () => {
    if (!window.confirm(`Reset the sent-tracker for "${city}"?\n\nNext blast can resend to anyone, including contacts who already received the invite this round.`)) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/marketing/auto-blast/reset?city=${encodeURIComponent(city.trim())}`,
        null, { withCredentials: true },
      );
      toast.success(`Reset "${city}" — next blast starts fresh.`);
      await refreshState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    }
  };

  // Test-delivery input — fires one WhatsApp invite to verify routing
  // before the admin commits to a large blast. Name is required so the
  // {name} variable in the template is never blank during the test.
  const [testPhone, setTestPhone] = useState('');
  const [testName, setTestName] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const sendTest = async () => {
    const name = (testName || '').trim();
    if (!name) {
      toast.error('Enter a recipient name — it fills the {name} variable in the template.');
      return;
    }
    const cleaned = (testPhone || '').replace(/\D/g, '');
    const phone10 = cleaned.length === 12 && cleaned.startsWith('91') ? cleaned.slice(2)
                  : cleaned.length === 11 && cleaned.startsWith('0') ? cleaned.slice(1)
                  : cleaned;
    if (phone10.length !== 10 || !/^[6-9]/.test(phone10)) {
      toast.error('Enter a valid 10-digit Indian mobile (starts with 6/7/8/9).');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/invite`,
        { name, phone: phone10, template },
        { withCredentials: true },
      );
      setTestResult(data);
      if (data?.ok) {
        toast.success(`Test sent to ${name} (+91 ${phone10}). Check your phone now.`, { duration: 9000 });
      } else {
        toast.error(`MSG91 said: ${data?.error_snippet || 'send failed'}`, { duration: 12000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const sentPct = state?.total_in_city
    ? Math.round((state.sent_this_round / state.total_in_city) * 100)
    : 0;

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-auto-blast-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <PaperPlaneTilt size={20} weight="bold" className="text-brand-primary" />
          Auto WhatsApp Blast (City-wise, segment-proportional)
        </CardTitle>
        <CardDescription>
          Pick a city and the number of messages — we split them across professions in
          proportion to each segment&apos;s share, skip already-invited contacts in this round,
          and auto-start a new round once everyone in the city has been reached.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Template picker — applies to BOTH the test send and the blast */}
        <div className="border border-[#E8E1D5] rounded p-3 bg-sand-50 space-y-2" data-testid="auto-blast-template-picker">
          <Label className="text-xs font-bold text-text-secondary">
            WhatsApp Template
          </Label>
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className="w-full h-9 rounded-none border border-[#E8E1D5] px-2 text-sm bg-white"
            data-testid="auto-blast-template-select"
          >
            {TEMPLATES.map((t) => (
              <option key={t.name} value={t.name}>{t.label}</option>
            ))}
          </select>
          <pre className="text-[11px] text-text-secondary whitespace-pre-wrap font-sans bg-white border border-[#E8E1D5] p-2 rounded leading-snug no-translit" data-no-translit="true" data-testid="auto-blast-template-preview">
            {currentTpl.preview.replace('{name}', testName.trim() || '[Recipient Name]')}
          </pre>
          <p className="text-[10px] text-text-secondary">
            Template name <code className="px-1 bg-stone-100">{currentTpl.name}</code> must already be approved in your MSG91 dashboard. Language code sent to MSG91 is <code className="px-1 bg-stone-100">en</code> (Meta-registered language, not body-text language — Tamil templates are typically registered under <code className="px-1 bg-stone-100">en</code>).
          </p>
          <p className="text-[10px] text-text-secondary leading-snug">
            <strong>Variable <code className="px-1 bg-stone-100">{'{name}'}</code></strong> is auto-filled per contact: each contact&apos;s saved business name (e.g. &ldquo;Royal Decorators&rdquo;) is sent. If a contact has no name, the segment label (e.g. &ldquo;Decorator&rdquo;) is used as a friendly fallback — never blank, never &ldquo;Vendor&rdquo;.
          </p>
        </div>

        {/* Test delivery — fire one message to confirm MSG91 + template
            wiring before launching the real blast. */}
        <div className="border border-blue-200 bg-blue-50 rounded p-3 space-y-2" data-testid="auto-blast-test-panel">
          <Label className="text-xs font-bold text-blue-900 flex items-center gap-1">
            <PaperPlaneTilt size={12} weight="fill" />
            Test delivery — send one invite to verify routing
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-blue-900 uppercase tracking-wider">
                Recipient name <span className="text-red-600">*</span>
              </Label>
              <Input value={testName} onChange={(e) => setTestName(e.target.value)}
                placeholder="e.g. Royal Decorators"
                className="rounded-none border-blue-200 h-9"
                data-testid="auto-blast-test-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-blue-900 uppercase tracking-wider">
                Phone <span className="text-red-600">*</span>
              </Label>
              <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
                placeholder="10-digit mobile (e.g. 9876543210)"
                className="rounded-none border-blue-200 h-9 font-mono"
                data-testid="auto-blast-test-phone" />
            </div>
            <Button onClick={sendTest}
              disabled={testing || !testPhone.trim() || !testName.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-none h-9 disabled:opacity-50 self-end"
              data-testid="auto-blast-test-btn">
              {testing ? 'Sending…' : 'Send test'}
            </Button>
          </div>
          {testName.trim() && (
            <div className="text-[11px] text-blue-900 bg-white border border-blue-100 rounded p-2 no-translit" data-no-translit="true" data-testid="auto-blast-test-name-preview">
              <span className="font-bold">Will send:</span> {currentTpl.preview.replace('{name}', testName.trim())}
            </div>
          )}
          {testResult && (
            <div className={`text-[11px] ${testResult.ok ? 'text-green-700' : 'text-red-700'}`} data-testid="auto-blast-test-result">
              {testResult.ok
                ? `Sent. MSG91 request_id: ${testResult.log?.msg91_request_id || '—'}`
                : `Failed: ${testResult.log?.error_snippet || 'unknown error'}`}
            </div>
          )}
          <p className="text-[10px] text-blue-900">
            Sends the template selected above with your typed name. Doesn&apos;t touch the round tracker.
          </p>
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto_auto] gap-2">
          <div className="space-y-1">
            <Label className="text-xs font-bold text-text-secondary">City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)}
              list="auto-blast-cities"
              placeholder="Chennai"
              className="rounded-none border-[#E8E1D5] h-9"
              data-testid="auto-blast-city-input" />
            <datalist id="auto-blast-cities">
              {allCities.map((c) => <option key={c.city} value={c.city} />)}
            </datalist>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-bold text-text-secondary">No. of msgs</Label>
            <Input type="number" min="1" max="2000"
              value={total} onChange={(e) => setTotal(Math.max(1, parseInt(e.target.value || '0', 10)))}
              className="rounded-none border-[#E8E1D5] h-9 font-mono text-right"
              data-testid="auto-blast-total-input" />
          </div>
          <Button onClick={runPreview} variant="outline"
            className="rounded-none border-[#E8E1D5] h-9 self-end mt-[20px]"
            data-testid="auto-blast-preview-btn">
            <ChartBar size={14} className="mr-1" /> Preview split
          </Button>
          <Button onClick={runBlast} disabled={running}
            className="bg-green-600 hover:bg-green-700 text-white rounded-none h-9 self-end mt-[20px]"
            data-testid="auto-blast-run-btn">
            <PaperPlaneTilt size={14} weight="fill" className="mr-1" />
            {running ? 'Blasting…' : `Blast ${total}`}
          </Button>
        </div>

        {/* City state */}
        {state && (
          <div className="border border-[#E8E1D5] rounded p-3 bg-sand-50 space-y-2" data-testid="auto-blast-state">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-1">
                <MapPin size={14} className="text-brand-primary" />
                <span className="font-bold text-text-primary">{state.city}</span>
                <span className="text-text-secondary">· {state.total_in_city} contacts</span>
              </div>
              <button onClick={resetCity}
                className="text-[11px] underline text-red-600 hover:text-red-700"
                data-testid="auto-blast-reset-btn">
                <ArrowsClockwise size={11} className="inline mr-0.5" />Reset round
              </button>
            </div>
            <div>
              <div className="flex items-center justify-between text-[11px] text-text-secondary mb-0.5">
                <span>Round progress · {state.sent_this_round}/{state.total_in_city}</span>
                <span>{state.rounds_completed} full round{state.rounds_completed === 1 ? '' : 's'} done</span>
              </div>
              <div className="h-2 bg-white border border-[#E8E1D5] rounded-full overflow-hidden">
                <div className="h-full bg-brand-primary transition-all"
                  style={{ width: `${sentPct}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* Preview / allocation table — Allocated column is editable */}
        {(preview || result) && (
          <div className="border border-[#E8E1D5] rounded max-h-[360px] overflow-y-auto" data-testid="auto-blast-allocation">
            <table className="w-full text-xs">
              <thead className="bg-sand-100 text-[10px] uppercase tracking-wider text-text-secondary sticky top-0">
                <tr>
                  <th className="p-2 text-left">Segment</th>
                  <th className="p-2 text-right">Share</th>
                  <th className="p-2 text-right w-24">Allocated</th>
                  <th className="p-2 text-right">Sent</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries((preview || result).allocation || {}).map(([seg, alloc]) => {
                  const segCount = state?.segments?.find((s) => s.profession === seg)?.count || 0;
                  const share = state?.total_in_city
                    ? Math.round((segCount / state.total_in_city) * 100)
                    : 0;
                  return (
                    <tr key={seg} className="border-t border-[#E8E1D5]">
                      <td className="p-2">{seg}</td>
                      <td className="p-2 text-right tabular-nums text-text-secondary">{share}% ({segCount})</td>
                      <td className="p-2 text-right">
                        {preview ? (
                          <input
                            type="number"
                            min="0"
                            max={segCount}
                            value={allocOverride[seg] ?? String(alloc)}
                            onChange={(e) => setAllocOverride((prev) => ({ ...prev, [seg]: e.target.value }))}
                            className="w-16 h-7 px-1 text-right tabular-nums font-bold border border-[#E8E1D5] focus:border-brand-primary focus:outline-none"
                            data-testid={`auto-blast-alloc-${seg.replace(/\s+/g, '-')}`}
                          />
                        ) : (
                          <span className="tabular-nums font-bold">{alloc}</span>
                        )}
                      </td>
                      <td className="p-2 text-right tabular-nums text-green-700">
                        {result ? (alloc > 0 ? alloc : '—') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {preview && (
                <tfoot className="bg-sand-100 sticky bottom-0">
                  <tr className="border-t-2 border-[#E8E1D5]">
                    <td className="p-2 font-bold text-text-primary" colSpan={2}>
                      Total (editable)
                    </td>
                    <td className="p-2 text-right">
                      <span className={`font-bold tabular-nums ${allocSum === total ? 'text-text-primary' : 'text-amber-700'}`} data-testid="auto-blast-alloc-sum">
                        {allocSum}
                      </span>
                      {allocSum !== total && (
                        <span className="ml-1 text-[10px] text-amber-700">
                          ({allocSum > total ? '+' : ''}{allocSum - total} vs {total})
                        </span>
                      )}
                    </td>
                    <td className="p-2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {preview && allocSum !== total && (
          <p className="text-[11px] text-amber-700 -mt-2">
            You edited the split. Clicking <strong>Blast</strong> will send <strong>{allocSum}</strong> messages
            (your row totals), not the {total} typed above.
          </p>
        )}

        {result && (
          <div className="p-3 border border-green-300 bg-green-50 rounded text-sm" data-testid="auto-blast-result">
            <div className="font-bold text-green-900 flex items-center gap-1">
              <ListChecks size={14} weight="bold" />
              Blast complete
            </div>
            <div className="text-xs text-green-900 mt-1">
              Sent <strong>{result.sent_ok}</strong> · Failed <strong>{result.failed}</strong> ·
              {' '}Round progress: <strong>{result.sent_this_round_after}/{result.total_in_city}</strong>
              {result.round_complete && (
                <span className="ml-1 text-amber-800">· Round COMPLETE — next blast starts a new round.</span>
              )}
              {result.auto_reset_happened && (
                <span className="ml-1 text-blue-800">· Auto-reset triggered (previous round already covered everyone).</span>
              )}
            </div>
          </div>
        )}

        <p className="text-[11px] text-text-secondary border-l-2 border-brand-primary/30 pl-2">
          Each invite uses the saved contact&apos;s name in the selected WhatsApp template.
          Already-sent phones in the current round are skipped automatically.
          Coverage resets when all city contacts have received the invite OR when you click &quot;Reset round&quot;.
        </p>
      </CardContent>
    </Card>
  );
};

export default AdminAutoBlastCard;
